import React, { useEffect, useState } from 'react'
import { useRouteError } from 'react-router-dom'
import { logInfo } from '@tomra/datadog-browser-logging'
import type { FallbackProps } from 'react-error-boundary'
import { Button } from '@tomra/design-system-react/button'

const ErrorFallback = ({ errorMsg, onReset }: { errorMsg: string; onReset: () => void }) => {
  const [isLoading, setIsLoading] = useState(false)

  const onResetButton = () => {
    setIsLoading(true)
    onReset()
  }

  return (
    <div className="min-h-[100vh] bg-amber-400 flex flex-col items-center justify-around p-9 text-center">
      {errorMsg.match(/(XHR error)|(failed to fetch)|(fetch error)|(aborted)|(request failed)|(load failed)/gi) ? (
        <>
          <span className="icon-[fluent--wifi-off-24-regular] size-24" />
          <p>Seems we have some trouble fetching your data. Please check your internet connection, and try again.</p>
        </>
      ) : (
        <>
          <span className="icon-[fluent--error-circle-24-regular] size-24" />
          <p>Whoopsie! Something went wrong. Please try again later.</p>
          <p className="p-4 rounded-lg bg-rose-400/40 text-sm min-w-screen-sm">{errorMsg}</p>
        </>
      )}

      <Button variant="primary" className="min-w-screen-sm" onClick={onResetButton}>
        {isLoading ? <div className="loadingSpinner" /> : <span>Reload</span>}
      </Button>
    </div>
  )
}

export const onGeneralErrorBoundary = (error: unknown) => {
  const errorMsg = typeof error === 'string' ? error : (error as Error)?.message
  logInfo('General Error Boundary shown', { errorMsg })
}

export const GeneralErrorBoundary = ({ error, resetErrorBoundary }: FallbackProps) => {
  const errorMsg = typeof error === 'string' ? error : (error as Error)?.message
  return <ErrorFallback errorMsg={errorMsg} onReset={resetErrorBoundary} />
}

export const RouterErrorBoundary = () => {
  const error = useRouteError() as unknown
  const errorMsg = typeof error === 'string' ? error : (error as Error)?.message

  useEffect(() => {
    logInfo('Router Error Boundary shown', { errorMsg })
  }, [errorMsg])

  return <ErrorFallback errorMsg={errorMsg} onReset={() => (window.location.href = '/')} />
}
